import { AcademicLeague } from "../types/battle"
import { ControlTier, WarStatus } from "../types/faction"
import { isProfileFrameKey, resolveProfileFrame } from "../constants/profileFrames"

/** Рамки профиля — с HEGEMONY (≥70% war pts города). */
export const PROFILE_FRAME_MIN_TIER: ControlTier = "HEGEMONY"
export const PROFILE_FRAME_CHEST_MIN_TIER: ControlTier = "TOTAL"

const FRAME_ELIGIBLE_TIERS: ControlTier[] = ["HEGEMONY", "TOTAL"]

/**
 * Бэкенд (resolvePlayerProfileFrameKey) отдаёт уже вычисленный profileFrameKey:
 * null при tier < HEGEMONY или если лига не доминирует. Сырых «залипших» значений из БД нет.
 *
 * resolveEffectiveProfileFrameKey — доп. фильтр, когда есть локальный war-status
 * (War dashboard, превью influence). Для passport/public/leaderboard/map/battle
 * доверяем API и применяем только sanitizeProfileFrameForLeague.
 */

/** Defense-in-depth: рамка должна совпадать с academicLeague игрока. */
export function sanitizeProfileFrameForLeague(
  profileFrameKey: string | null | undefined,
  academicLeague: AcademicLeague | null | undefined
): string | null {
  if (!profileFrameKey || !isProfileFrameKey(profileFrameKey)) return null
  if (!academicLeague) return profileFrameKey
  const frame = resolveProfileFrame(profileFrameKey)
  if (!frame || frame.league !== academicLeague) return null
  return profileFrameKey
}

/**
 * Скрывает profileFrameKey, если локальный war-status не даёт рамку:
 * tier < 70%, нет dominantLeague, лига ≠ лидер, chest при tier < 80%.
 */
export function resolveEffectiveProfileFrameKey(
  profileFrameKey: string | null | undefined,
  academicLeague: AcademicLeague | null | undefined,
  war: WarStatus | null | undefined
): string | null {
  if (!profileFrameKey || !isProfileFrameKey(profileFrameKey)) return null
  if (!war) return profileFrameKey

  const tier = war.controlTier?.tier
  if (!tier || !FRAME_ELIGIBLE_TIERS.includes(tier)) return null

  const dominantLeague = war.dominantLeague
  if (!dominantLeague) return null

  if (!academicLeague || academicLeague !== dominantLeague) return null

  const frame = resolveProfileFrame(profileFrameKey)
  if (!frame || frame.league !== academicLeague) return null

  if (frame.isChest && tier !== PROFILE_FRAME_CHEST_MIN_TIER) return null

  return profileFrameKey
}

export function isProfileFrameTier(tier: ControlTier | null | undefined): boolean {
  return tier != null && FRAME_ELIGIBLE_TIERS.includes(tier)
}

/** Подсказка, когда лига лидирует, но до рамки ещё не добрались. */
export function formatProfileFramePendingHint(war: WarStatus): string | null {
  if (!war.dominantLeague || !isProfileFrameTier(war.controlTier?.tier)) {
    const leading =
      war.dominantLeague === "TITAN"
        ? war.titanPercent
        : war.dominantLeague === "ATLANT"
          ? war.atlantPercent
          : null
    if (war.dominantLeague && leading != null && leading < 70) {
      return `Лига лидирует (${leading}%), но рамка профиля откроется при ≥70% (Гегемония). Сейчас: ${war.controlTier.label}.`
    }
    return null
  }
  return null
}
